import React, { useState } from 'react';
import { Button } from './ui/button';
import { ArrowLeft, ArrowRight } from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const SCALE_LABELS = ['Raramente', 'Às vezes', 'Metade das vezes', 'Com frequência', 'Quase sempre'];

const SECTION_A = [
  'Quando algo me incomoda numa relação de trabalho, consigo falar sobre isso com a pessoa envolvida.',
  'Consigo discordar de colegas ou gestores sem minimizar o que penso.',
  'Quando percebo tensão numa relação, nomeio isso em vez de deixar passar.',
  'Consigo trazer dificuldades relacionais à tona mesmo quando o ambiente parece difícil para isso.',
  'Quando erro em algo que afeta alguém, consigo reconhecer isso sem esperar que a pessoa perceba sozinha.',
  'Quando estou em conflito com alguém, consigo ouvir o ponto de vista dela antes de defender o meu.',
  'Depois de um conflito, consigo manter ou recuperar a qualidade do vínculo.',
  'Consigo identificar o que a outra pessoa pode estar precisando — mesmo quando o comportamento dela me irrita.',
  'Conflitos que atravesso costumam terminar com algum aprendizado, não só com desgaste.',
  'Consigo sustentar conversas difíceis sem precisar me afastar da pessoa ou encerrar a relação.',
];

const SECTION_B = [
  'Quando alguém comete um erro, a reação do grupo tende a ser de aprendizagem, não de punição.',
  'É possível expressar discordância aqui sem prejudicar a relação com quem discordei.',
  'Quando alguém traz um problema ou tensão ao grupo, a resposta costuma ser de escuta.',
  'Vozes menos frequentes no time têm espaço real para aparecer nas conversas coletivas.',
  'Conflitos abertos são vistos aqui como parte do trabalho — não como falhas de convivência.',
  'Quando alguém se expõe ou assume um risco relacional aqui, isso não é usado contra ela depois.',
  'Este ambiente me dá segurança para ser honesta, mesmo quando é desconfortável.',
  'Acredito que minha percepção sobre o ambiente aqui é parecida com a da maioria das minhas colegas.',
];

const TOTAL_ITEMS = SECTION_A.length + SECTION_B.length; // 18

const QUADRANT_INFO = {
  green:  { label: 'Verde',   desc: 'Alta qualidade relacional — individual e coletiva.' },
  blue:   { label: 'Azul',    desc: 'Ambiente coletivo forte, com espaço para desenvolver expressão individual.' },
  amber:  { label: 'Âmbar',   desc: 'Expressão individual presente, com oportunidade de fortalecer o ambiente coletivo.' },
  red:    { label: 'Vermelho', desc: 'Ponto de atenção — tanto individual quanto coletivo merecem cuidado.' },
};

const IQRSurveyModal = ({ onClose }) => {
  const [step, setStep]           = useState('intro');   // intro | section_a | section_b | done
  const [currentIdx, setCurrentIdx] = useState(0);
  const [scoresA, setScoresA]     = useState(Array(10).fill(null));
  const [scoresB, setScoresB]     = useState(Array(8).fill(null));
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError]         = useState('');
  const [result, setResult]       = useState(null);

  const inA = step === 'section_a';
  const inB = step === 'section_b';
  const items = inA ? SECTION_A : SECTION_B;
  const scores = inA ? scoresA : scoresB;
  const currentText = items[currentIdx];
  const currentValue = scores[currentIdx];
  const isLastInSection = currentIdx === items.length - 1;
  const isFirstInSection = currentIdx === 0;

  // global progress (1-based)
  const globalIdx = inA ? currentIdx : SECTION_A.length + currentIdx;

  const setScore = (v) => {
    if (inA) {
      setScoresA(prev => { const a = [...prev]; a[currentIdx] = v; return a; });
    } else {
      setScoresB(prev => { const a = [...prev]; a[currentIdx] = v; return a; });
    }
  };

  const handleNext = () => {
    if (isLastInSection) {
      if (inA) { setStep('section_b'); setCurrentIdx(0); }
      // if inB: submit
    } else {
      setCurrentIdx(i => i + 1);
    }
  };

  const handleBack = () => {
    if (isFirstInSection) {
      if (inA) { setStep('intro'); }
      else { setStep('section_a'); setCurrentIdx(SECTION_A.length - 1); }
    } else {
      setCurrentIdx(i => i - 1);
    }
  };

  const handleSubmit = async () => {
    setIsSubmitting(true);
    setError('');
    try {
      const res = await axios.post(
        `${API}/surveys/iqr`,
        { section_a: scoresA, section_b: scoresB },
        { withCredentials: true }
      );
      setResult(res.data);
      setStep('done');
    } catch (e) {
      setError('Não foi possível enviar. Tente novamente.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const progressPct = ((globalIdx) / TOTAL_ITEMS) * 100;
  const canAdvance = currentValue !== null;

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg flex flex-col max-h-[90vh]">

        {/* Header */}
        <div className="p-5 border-b flex items-start justify-between flex-shrink-0">
          <div className="flex-1">
            <p className="text-xs font-semibold text-purple-600 uppercase tracking-wide mb-0.5">
              Índice de Qualidade Relacional
            </p>
            {(inA || inB) ? (
              <>
                <div className="flex items-center justify-between text-xs text-gray-400 mb-1 mt-1">
                  <span>{inA ? 'Seção A — Como eu me movo nas relações' : 'Seção B — Como percebo o ambiente'}</span>
                  <span>{globalIdx + 1} de {TOTAL_ITEMS}</span>
                </div>
                <div className="w-full bg-gray-100 rounded-full h-1.5">
                  <div
                    className="bg-purple-500 h-1.5 rounded-full transition-all duration-300"
                    style={{ width: `${progressPct}%` }}
                  />
                </div>
              </>
            ) : (
              <h2 className="text-lg font-bold text-gray-900 mt-0.5">
                {step === 'done' ? 'Avaliação concluída' : 'Como eu me movo nas relações'}
              </h2>
            )}
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 text-2xl leading-none ml-4 flex-shrink-0"
            aria-label="Fechar"
          >×</button>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto p-5">

          {/* Intro */}
          {step === 'intro' && (
            <div className="space-y-4">
              <p className="text-gray-700 leading-relaxed">
                Sua gestora abriu uma rodada de avaliação relacional. Suas respostas são completamente anônimas — ela verá apenas dados agregados do grupo.
              </p>
              <p className="text-gray-700 leading-relaxed">
                A <strong>Seção A</strong> é sobre como você se move nas relações. A <strong>Seção B</strong> é sobre como você percebe o ambiente do grupo.
              </p>
              <p className="text-sm text-gray-500">
                Escala: <strong>1 = Raramente</strong> → <strong>5 = Quase sempre</strong> · 18 afirmações · ~5 minutos
              </p>
              <div className="bg-purple-50 rounded-xl p-4">
                <p className="text-sm text-purple-800 font-medium">
                  Sem resposta certa ou errada. O que vale é o que está presente para você agora.
                </p>
              </div>
              <Button
                onClick={() => { setStep('section_a'); setCurrentIdx(0); }}
                className="w-full bg-purple-600 hover:bg-purple-700 text-white mt-4"
              >
                Começar
              </Button>
            </div>
          )}

          {/* One item at a time */}
          {(inA || inB) && (
            <div className="space-y-5">
              <div className={`p-4 rounded-xl ${inA ? 'bg-purple-50' : 'bg-blue-50'}`}>
                <p className={`text-sm font-semibold leading-relaxed ${inA ? 'text-purple-900' : 'text-blue-900'}`}>
                  {currentText}
                </p>
              </div>

              <div className="space-y-2">
                {[1, 2, 3, 4, 5].map((score) => (
                  <button
                    key={score}
                    onClick={() => setScore(score)}
                    className={`w-full flex items-center gap-3 px-4 py-3 rounded-xl border-2 text-sm transition-all ${
                      currentValue === score
                        ? 'bg-purple-600 border-purple-600 text-white font-medium'
                        : 'bg-white border-gray-200 text-gray-700 hover:border-purple-300 hover:bg-purple-50'
                    }`}
                  >
                    <span className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold flex-shrink-0 ${
                      currentValue === score ? 'bg-white text-purple-600' : 'bg-gray-100 text-gray-500'
                    }`}>{score}</span>
                    <span>{SCALE_LABELS[score - 1]}</span>
                  </button>
                ))}
              </div>

              {error && <p className="text-sm text-red-500">{error}</p>}
            </div>
          )}

          {/* Done */}
          {step === 'done' && (
            <div className="text-center py-4 space-y-5">
              <div className="text-5xl">🌿</div>
              <div>
                <h3 className="text-xl font-bold text-gray-900 mb-1">Obrigada por participar.</h3>
                <p className="text-sm text-gray-500 leading-relaxed">
                  Sua resposta foi registrada. Ela contribui para uma leitura coletiva do grupo — sem identificar nenhuma pessoa individualmente.
                </p>
              </div>

              {result && (
                <div className="bg-purple-50 rounded-xl p-4 text-left space-y-3">
                  <p className="text-xs font-semibold text-purple-700 uppercase tracking-wide">Seu resultado</p>
                  <div className="grid grid-cols-2 gap-3">
                    <div className="bg-white rounded-lg p-3 text-center border border-purple-100">
                      <p className="text-xs text-gray-500 mb-0.5">Seção A</p>
                      <p className="text-2xl font-bold text-purple-700">
                        {result.score_a ? result.score_a.toFixed(1) : '—'}
                      </p>
                      <p className="text-xs text-gray-400">/ 5.0</p>
                    </div>
                    <div className="bg-white rounded-lg p-3 text-center border border-blue-100">
                      <p className="text-xs text-gray-500 mb-0.5">Seção B</p>
                      <p className="text-2xl font-bold text-blue-700">
                        {result.score_b ? result.score_b.toFixed(1) : '—'}
                      </p>
                      <p className="text-xs text-gray-400">/ 5.0</p>
                    </div>
                  </div>
                  {result.quadrante && QUADRANT_INFO[result.quadrante] && (
                    <div className="bg-white rounded-lg p-3 border border-gray-100">
                      <p className="text-xs text-gray-500 mb-0.5">Quadrante</p>
                      <p className="text-sm font-semibold text-gray-800">
                        {QUADRANT_INFO[result.quadrante].label}
                      </p>
                      <p className="text-xs text-gray-500 mt-0.5">
                        {QUADRANT_INFO[result.quadrante].desc}
                      </p>
                    </div>
                  )}
                </div>
              )}

              <Button onClick={onClose} className="w-full bg-purple-600 hover:bg-purple-700 text-white">
                Fechar
              </Button>
            </div>
          )}
        </div>

        {/* Footer navigation */}
        {(inA || inB) && (
          <div className="p-5 border-t flex justify-between items-center flex-shrink-0">
            <Button variant="outline" onClick={handleBack} className="flex items-center gap-1">
              <ArrowLeft className="w-4 h-4" />
              Anterior
            </Button>

            {isLastInSection && inB ? (
              <Button
                onClick={handleSubmit}
                disabled={!canAdvance || isSubmitting}
                className="bg-purple-600 hover:bg-purple-700 text-white"
              >
                {isSubmitting ? 'Enviando...' : 'Enviar avaliação'}
              </Button>
            ) : (
              <Button
                onClick={handleNext}
                disabled={!canAdvance}
                className="bg-purple-600 hover:bg-purple-700 text-white flex items-center gap-1"
              >
                {isLastInSection && inA ? 'Próxima seção' : 'Próxima'}
                <ArrowRight className="w-4 h-4" />
              </Button>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default IQRSurveyModal;
